import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IMessageInfo, IMultiSuggestResult } from '@cian/geosuggest-widget';

import {
  ECurrentErrorField,
  IErrorTooltip,
  IFormFields,
  IFlatAreasSuggest,
  INewbuildingSectionItem,
} from '../../types/formFields';
import { EFlatRoomCounts } from '../../../common/utils/types';

const initialState: IFormFields = {
  address: '',
  userSetArea: false,
  userSetRoomsCount: false,
  errorTooltip: {
    currentErrorField: ECurrentErrorField.none,
    errorText: '',
  },
  area: undefined,
  roomsCount: undefined,
  flatNumber: undefined,
  landArea: undefined,
  houseArea: undefined,
  newbuildingSection: undefined,
  newbuildingSectionsList: undefined,
  addressGeoSuggest: {
    suggestData: undefined,
    isFetching: false,
    error: undefined,
  },
  flatAreasSuggest: {
    flatAreas: undefined,
  },
  flatNumberSuggest: {
    isSuccess: false,
  },
};

const formFieldsSlice = createSlice({
  initialState,
  name: 'formFieldsSlice',
  reducers: {
    setAddress: (state, action: PayloadAction<string>) => {
      state.address = action.payload;
    },
    setRooms: (state, action: PayloadAction<EFlatRoomCounts | undefined>) => {
      state.roomsCount = action.payload;
    },
    setUserRoomsCountFlag: (state, action: PayloadAction<boolean>) => {
      state.userSetRoomsCount = action.payload;
    },
    setArea: (state, action: PayloadAction<number | undefined>) => {
      state.area = action.payload;
    },
    setUserAreaFlag: (state, action: PayloadAction<boolean>) => {
      state.userSetArea = action.payload;
    },
    setFlatNumber: (state, action: PayloadAction<number | undefined>) => {
      state.flatNumber = action.payload;
    },
    setHouseId: (state, action: PayloadAction<number>) => {
      state.houseId = action.payload;
    },
    setLandArea: (state, action: PayloadAction<number>) => {
      state.landArea = action.payload;
    },
    setHouseArea: (state, action: PayloadAction<number>) => {
      state.houseArea = action.payload;
    },
    setFlatAreasSuccessResponse: (state, action: PayloadAction<IFlatAreasSuggest>) => {
      state.flatAreasSuggest = action.payload;
    },
    setFlatNumberSuccessResponse: (state, action: PayloadAction<boolean>) => {
      state.flatNumberSuggest = {
        isSuccess: action.payload,
      };
    },
    setAddressGeoSuggestRequest: state => {
      state.addressGeoSuggest = {
        ...state.addressGeoSuggest,
        isFetching: true,
        error: undefined,
      };
    },
    setAddressGeoSuggestSuccess: (state, action: PayloadAction<IMultiSuggestResult>) => {
      state.addressGeoSuggest = {
        ...state.addressGeoSuggest,
        isFetching: false,
        error: undefined,
        suggestData: action.payload,
      };
    },
    setAddressGeoSuggestFailure: (state, action: PayloadAction<IMessageInfo>) => {
      state.addressGeoSuggest = {
        ...state.addressGeoSuggest,
        suggestData: undefined,
        isFetching: false,
        error: action.payload,
      };
    },
    setNewbuildingSection: (state, action: PayloadAction<number>) => {
      state.newbuildingSection = action.payload;
    },
    setNewbuildingSectionsList: (state, action: PayloadAction<INewbuildingSectionItem[]>) => {
      state.newbuildingSectionsList = action.payload;
    },
    setError: (state, action: PayloadAction<IErrorTooltip>) => {
      state.errorTooltip = {
        currentErrorField: action.payload.currentErrorField,
        errorText: action.payload.errorText,
      };
    },
    resetForm: () => initialState,
  },
});

export const {
  reducer: formFieldsReducer,
  actions: {
    resetForm,
    setAddress,
    setArea,
    setHouseArea,
    setLandArea,
    setHouseId,
    setAddressGeoSuggestFailure,
    setAddressGeoSuggestRequest,
    setAddressGeoSuggestSuccess,
    setFlatNumberSuccessResponse,
    setFlatAreasSuccessResponse,
    setRooms,
    setFlatNumber,
    setUserAreaFlag,
    setUserRoomsCountFlag,
    setNewbuildingSection,
    setNewbuildingSectionsList,
    setError,
  },
} = formFieldsSlice;
